// ATLAS-5792: guard for a content-loss defect in CKEditor 5 48.1.1.
//
// `upcastTableAlignedDiv` in @ckeditor/ckeditor5-table 48.1.1 runs on `element:div`. `getViewTableFromWrapper` matches a
// div[align] that has ANY direct <table> child, the converter consumes the div, converts the table, and `convertChildren`
// then drops every non-table sibling. A div[align] holding a table among other content therefore loses that content.
//
// This guard hooks the (decorated) DataController `toModel` event at highest priority and, before any upcast runs, moves
// every direct <table> of a mixed-content div[align] into its own synthesized `div[align]` wrapper. The upstream converter
// then sees a sole-table wrapper (today's behaviour, alignment included) and the outer div is converted by GHS with all
// its content. Divs whose only content is a table are not touched.
//
// SYNC: This file exists in two byte-identical copies:
//   Frontend/Atlas.Spa.Backoffice/src/app/shared/widgets/ckeditor-aligned-div-table-guard.ts
//   Frontend/Atlas.Spa.Portal/src/app/shared/widgets/ckeditor-aligned-div-table-guard.ts
// Keep them in sync; any change goes into both (operator decision 2026-10-13: no shared front-end module).
//
// REMOVAL CONDITION: delete both copies, and their registrations in ckeditor-config.ts, once an upgraded CKEditor's
// `upcastTableAlignedDiv` only matches a div whose sole non-whitespace child is a table. Confirm it in that version's
// @ckeditor/ckeditor5-table source, and by repeating the mixed-content round trip without the guard. The version test
// (ckeditor-aligned-div-table-guard.spec.ts, G1) fails on any ckeditor5 bump to force this check.
//
// No runtime imports: ViewUpcastWriter comes from the runtime object passed to the factory (cloud.CKEditor in production).
import type {
  PluginConstructor,
  ViewDocumentFragment,
  ViewElement,
  ViewNode,
  ViewUpcastWriter,
} from 'https://cdn.ckeditor.com/typings/ckeditor5.d.ts';

/** CKEditor version whose upcastTableAlignedDiv this guard compensates for. */
export const ALIGNED_DIV_TABLE_GUARD_CKEDITOR_VERSION = '48.1.1';
export const ALIGNED_DIV_TABLE_GUARD_PLUGIN_NAME = 'AlignedDivTableGuard';

/** The only runtime class the guard needs, supplied from cloud.CKEditor (or, in tests, the npm ckeditor5 namespace). */
export interface AlignedDivTableGuardRuntime {
  ViewUpcastWriter: typeof ViewUpcastWriter;
}

// ASCII whitespace only. Not \s: that matches U+00A0, and &nbsp; must count as content.
const ASCII_WHITESPACE_ONLY = /^[ \t\n\f\r]*$/;

function isContentChild(child: ViewNode): boolean {
  if (child.is('$text')) {
    return !ASCII_WHITESPACE_ONLY.test((child as any).data);
  }
  if (child.is('uiElement') || child.is('element', '$comment')) {
    return false;
  }
  return true;
}

/** True for a div with an `align` attribute, at least one direct <table> child and at least one other direct content child. */
export function isMixedContentAlignedDiv(node: ViewNode | ViewDocumentFragment): boolean {
  if (!node.is('element', 'div') || !(node as ViewElement).hasAttribute('align')) {
    return false;
  }
  const children = Array.from((node as ViewElement).getChildren());
  const tableCount = children.filter(c => c.is('element', 'table')).length;
  if (tableCount === 0) {
    return false;
  }
  return tableCount > 1 || children.some(c => !c.is('element', 'table') && isContentChild(c));
}

/** Rewrites every matching div inside `root` (and `root` itself); returns the number of divs rewritten. */
export function isolateTablesInMixedContentAlignedDivs(root: ViewDocumentFragment | ViewElement, writer: ViewUpcastWriter): number {
  const targets: ViewElement[] = [];
  if (isMixedContentAlignedDiv(root)) {
    targets.push(root as ViewElement);
  }
  for (const { item } of writer.createRangeIn(root)) {
    if (isMixedContentAlignedDiv(item as ViewNode)) {
      targets.push(item as ViewElement);
    }
  }

  for (const div of targets) {
    const align = div.getAttribute('align') as string;
    const tables = Array.from(div.getChildren()).filter(c => c.is('element', 'table')) as ViewElement[];
    for (const table of tables) {
      const index = div.getChildIndex(table);
      writer.remove(table);
      writer.insertChild(index, writer.createElement('div', { align }, [table]), div);
    }
  }
  return targets.length;
}

/** Returns a function-style plugin (a `function`, not an arrow: CKEditor `new`s plugin constructors). */
export function createAlignedDivTableGuard(ck: AlignedDivTableGuardRuntime): PluginConstructor {
  function AlignedDivTableGuard(this: any, editor: any) {
    this.editor = editor;
  }
  AlignedDivTableGuard.pluginName = ALIGNED_DIV_TABLE_GUARD_PLUGIN_NAME;
  AlignedDivTableGuard.prototype.init = function (this: any) {
    const editor = this.editor;
    editor.data.on(
      'toModel',
      (_evt: unknown, [viewItem]: [ViewDocumentFragment | ViewElement]) => {
        isolateTablesInMixedContentAlignedDivs(viewItem, new ck.ViewUpcastWriter(editor.data.viewDocument));
      },
      { priority: 'highest' }
    );
  };
  AlignedDivTableGuard.prototype.destroy = function () {};
  return AlignedDivTableGuard as unknown as PluginConstructor;
}
